
import React, { useEffect, useState, useMemo } from "react";
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Table,
  PaginationItem,
  Pagination,
  PaginationLink,
} from "reactstrap";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import axiosInstance from "pages/Utility/axiosInstance";
import debounce from "lodash/debounce";
import { showError, showSuccess } from "helpers/notification_helper";
import Breadcrumb from "components/Common/Breadcrumb2";
import { uploadImage } from "helpers/item_helper";
import { MdDelete } from "react-icons/md";
import { FaRegEdit } from "react-icons/fa";
import { dangerIconStyle } from "styles/iconStyle";
import { getEncryptedLocal } from "pages/Utility/cookieUtils";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { ClipLoader } from "react-spinners";
import TableContainer from "components/Common/DataTableContainer";
import { t } from "i18next";

const ITEM_TYPES = [{ label: "Collection List", value: "Collection_List" }, { label: "Product List", value: "Product_List" }, { label: "Banners", value: "Banners" }, { label: "Sliders", value: "Sliders" }];
const LINK_TYPES = ["None", "Product", "Collection"];
const DIRECTIONS = ["horizontal", "vertical"];

const ItemList = () => {
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [visibleCount, setVisibleCount] = useState("");
  const [collections, setCollections] = useState([]);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingItems, setLoadingItems] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState()
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [parsed, setParsed] = useState();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productList,setProductList] = useState();
  const [selectedDirection, setSelectedDirection] = useState("");
  const [openModal, setOpenModal] = useState(false); // Modal visibility state
  const [itemToDelete, setItemToDelete] = useState(null); // Item to delete
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [totalPages, setTotalPages] = useState(1); // Total number of pages
  const [title_ar, setTitle_ar] = useState()
  const [sliders, setSliders] = useState([
    { file: "", linkType: "None", products: null, id: null },
  ]);
  const permissions = getEncryptedLocal("permissions");
  const canAddItem = permissions?.some(
    (item) => item?.permission_name === 'Home Layout Add'
  );
  const canEditItem = permissions?.some(
    (item) => item?.permission_name === 'Home Layout Edit'
  );
  const canDeleteItem = permissions?.some(
    (item) => item?.permission_name === 'Home Layout Delete'
  );

  /* ---------- EDIT MODE ---------- */
  const [editingId, setEditingId] = useState(null);
  const isEditMode = Boolean(editingId);

  /* ---------- FETCH ITEMS ---------- */
  const fetchItems = async () => {
    setLoadingItems(true);
    try {
      const res = await axiosInstance.get("V1/items");
      setTotalPages(res.data.data.total);
      setItems(res.data.data || []);
    } finally {
      setLoadingItems(false);
    }
  };

  /* ---------- COLLECTIONS ---------- */
  useEffect(() => {
    axiosInstance.get("V1/collections").then((res) => {
      setCollections(res.data.data || []);
    });
    fetchItems();
  }, []);

  /* ---------- PRODUCTS ---------- */
  const loadProducts = async (inputValue) => {
    const res = await axiosInstance.get("V1/products", {
      params: {
        search: inputValue,
        collectionId: selectedCollections?.value,
      },
    });
  
    return res.data.data.map((p) => ({
      label: p.title,
      value: p.id,
      price:p.price ? p.price : p.variants?.edges?.[0]?.node?.price,
      handle:p.handle,
      image: p.images.edges?.[0]?.node?.src
    }));
  };

  const debouncedLoadProducts = useMemo(
    () =>
      debounce((inputValue, callback) => {
        if (!selectedCollections || inputValue.length < 2) {
          callback([]);
          return;
        }
        loadProducts(inputValue).then(callback);
      }, 500),
    [selectedCollections]
  );

  /* ---------- SLIDER HELPERS ---------- */
  const handleSliderChange = (index, field, value) => {
    const updated = [...sliders];
    updated[index][field] = value;
    setSliders(updated);
  };
  const removeSlider = (index) => {
    setSliders((prev) => prev.filter((_, i) => i !== index));
  };
  /* ---------- VALIDATION ---------- */
  const validateForm = () => {
    const newErrors = {};

    if (!type) newErrors.type = "Type is required";
    if (!sortOrder) newErrors.sortOrder = "Sort order required";
    if (!title) newErrors.title = "Title required";
    if (!title_ar) newErrors.title_ar = "Title arabic is required"

    if (
      (type === "Collection_List" ||
        type === "Product_List" ||
        type === "Banners") &&
      !visibleCount
    ) {
      newErrors.visibleCount = "Visible items required";
    }

    if (type === "Collection_List" && !selectedCollections?.length) {
      newErrors.collections = "Select collections";
    }

    if (type === "Product_List") {
      if (!selectedCollections) newErrors.collections = "Select collection";
      if (!selectedProducts.length) newErrors.products = "Select products";
      if (!selectedDirection) newErrors.direction = "Direction required";
    }

    if (type === "Banners" || type === "Sliders") {
      sliders.forEach((s, i) => {
        const se = {};
        if (!s.file) se.file = "Image required";
        if (s.linkType === "Product" && !s.products)
          se.products = "Product required";
        if (s.linkType === "Collection" && !s.id)
          se.id = "Collection required";
        if (Object.keys(se).length) newErrors[`slider_${i}`] = se;
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  /* ---------- RESET ---------- */
  const resetForm = () => {
    setType("");
    setTitle("");
    setTitle_ar("");
    setSortOrder("");
    setVisibleCount("");
    setSelectedCollections([]);
    setSelectedProducts([]);
    setSelectedDirection("");
    setSliders([{ file: "", linkType: "None", products: null, id: null }]);
    setEditingId(null);
  };
  /* ---------- SUBMIT ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    let dataPayload = {};

    if (type === "Collection_List") {
      dataPayload = {
        title,
        title_ar,
        visibleCount,
        collectionIds: selectedCollections.map((c) => c.value),
      };
    }

    if (type === "Product_List") {
      dataPayload = {
        title,
        title_ar,
        direction: selectedDirection,
        visibleCount,
        collectionId: selectedCollections.value,
        productIds: selectedProducts.map(item=>({
          title:item?.label,
          id:item?.value,
          handle:item?.handle,
          price:item?.price,
          image:item?.image
        })),
      };
    }

    if (type === "Banners" || type === "Sliders") {
      dataPayload = {
        title,
        title_ar,
        visibleCount,
        items: sliders.map((s) => ({
          file: s.file,
          linkType: s.linkType,
          id:
            s.linkType === "Product"
              ? s.products?.value
              : s.linkType === "Collection"
                ? s.id
                : null,
        })),
      };
    }

    const payload = {
      type,
      sortOrder,
      data: JSON.stringify(dataPayload),
    };

    try {
      setLoading(true)
      if (isEditMode) {
        await axiosInstance.put(`V1/items/${editingId}`, payload);
        showSuccess("Item updated");
        setType("")
      } else {
        await axiosInstance.post("V1/items", payload);
        showSuccess("Item created");
        setType("")
      }
      setLoading(false);
      resetForm();
      fetchItems();
    } catch (err) {
      setLoading(false)
      showError(err.message);
    }
  };
  const addSlider = () => {
    setSliders((prev) => [
      ...prev,
      {
        file: "",
        linkType: "",
        products: null,
        id: "",
      },
    ]);
  };

  useEffect(() => {
    if (parsed?.collectionIds) {
      setSelectedCollections(
        parsed.collectionIds.map((cid) => ({
          label: collections.find((c) => c.id == cid)?.title,
          value: cid,
        }))
      );
    }

    if (parsed?.collectionId) {
      setSelectedDirection(parsed.direction);

      setSelectedCollections({
        label: collections.find((c) => c.id == parsed.collectionId)?.title,
        value: parsed.collectionId,
      });
    }

  }, [collections, parsed])

  // useEffect(()=>{
  //   if(type){
  //     setTitle("");
  //   setTitle_ar("");
  //   setSortOrder("");
  //   setVisibleCount("");
  //   setSelectedCollections([]);
  //   setSelectedProducts([]);
  //   setSelectedDirection("");
  //   setSliders([{ file: "", linkType: "None", products: null, id: null }]);
  //   setEditingId(null);
  //   }
  // },[type])

  /* ---------- EDIT ---------- */
  const handleEdit = async (id) => {
    try {
      const res = await axiosInstance.get(`V1/items/item/${id}`);
      const item = res.data.data;
      const parsed = JSON.parse(item.data);
      setParsed(parsed)
      setEditingId(id);
      setType(item.type);
      setSortOrder(item.sortOrder);
      setTitle(parsed.title || "");
      setTitle_ar(parsed.title_ar || "")
      setVisibleCount(parsed.visibleCount || "");

      if (item.type == "Collection_List") {

        setSelectedCollections(
          parsed?.collectionIds?.map((cid) => ({
            label: collections.find((c) => c.id == cid)?.title,
            value: cid,
          }))
        );
      }

      if (item.type === "Product_List") {
        setSelectedDirection(parsed.direction);

        setSelectedCollections({
          label: collections.find((c) => c.id == parsed.collectionId)?.title,
          value: parsed.collectionId,
        });

        // fetch product details to restore labels
        const restoredProducts = await Promise.all(
          parsed.productIds?.map(async (pid) => {
            try {
              const res = await axiosInstance.get(`V1/products/${pid?.id.split('/').pop()}`);
              const product = res.data.product || res.data.data;

              return {
                label: product.title,
                value: product.id ? `gid://shopify/Product/${product.id}` : pid?.id,
                handle:product?.handle,
                price:product?.price ? product?.price : product?.variants?.[0]?.price,
                image:product?.images?.[0]?.src,
              }; 
            } catch (err) {
              return {
                label: pid,
                value: pid,
              };
            }
          })
        );
        setSelectedProducts(restoredProducts);
      }


      if (item.type === "Banners" || item.type === "Sliders") {
        const restoredSliders = await Promise.all(
          parsed.items?.map(async (s) => {
            if (s.linkType === "Product" && s.id) {
              // fetch product for label
              const prodRes = await axiosInstance.get(`V1/products/${s.id.split('/').pop()}`);

              return {
                file: s.file,
                linkType: "Product",
                products: {
                  label: prodRes.data.product?.title,
                  value: s.id,
                },
                id: null,
              };
            }

            if (s.linkType === "Collection" && s.id) {
              return {
                file: s.file,
                linkType: "Collection",
                products: null,
                id: s.id,
              };
            }

            return {
              file: s.file,
              linkType: "None",
              products: null,
              id: null,
            };
          })
        );

        setSliders(restoredSliders);

      }

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.log(error)
      showError("Failed to load item");
    }
  };
  /* ---------- DELETE ---------- */
  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await axiosInstance.delete(`V1/items/${itemToDelete}`);
      showSuccess("Item deleted");
      fetchItems();
      setOpenModal(false);
    } catch (err) {
      showError("Failed to delete item");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Sort Order",
        accessorKey: "sortOrder",
        enableColumnFilter: false,
        enableSorting: true,
      },
      ...((canEditItem || canDeleteItem) ? [{
        header: "Actions",
        id: "actions",
        cell: ({ row }) => {
          return (
            <div className="actions d-flex align-items-center gap-2">
              {canEditItem && <Button
                color="primary"
                style={{ marginRight: "5px" }}
                onClick={() => handleEdit(row.original._id)}
              >
                <FaRegEdit size={18} />
              </Button>}{" "}
              {canDeleteItem && <Button
                size="sm"
                color="danger"
                onClick={() => {
                  setItemToDelete(row.original._id);
                  setOpenModal(true);
                }}
                style={dangerIconStyle}
              >
                <MdDelete size={18} />
              </Button>}
            </div>
          );
        },
      },
      ] : []),
    ],
    [canDeleteItem, canEditItem]
  );
  /* ---------- UI ---------- */
  return (
    <div className="page-content">
      <Breadcrumb title="Add Items" breadcrumbItems={[{ title: "Dashboard", link: "/dashboard" }, { title: 'Add items', link: "#" }]} />

      <div className="mt-3">
        {(canAddItem || canEditItem) && <Form style={{
          backgroundColor: "white",
          padding: "20px 20px",
          borderRadius: "8px",
        }} onSubmit={handleSubmit}>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Type *</Label>
                <Select
                  options={ITEM_TYPES}
                  value={ITEM_TYPES.filter(item=>item?.value == type)[0]}
                  onChange={(option) => setType(option?.value || "")}
                />
              <div className="text-danger">{errors.type}</div>
            </FormGroup>
          </Col>

          {(
            <Col md={4}>
              <FormGroup>
                <Label>Title *</Label>
                <Input placeholder="Enter title" value={title} style={{ borderColor: "hsl(0, 0%, 80%)" }} onChange={(e) => setTitle(e.target.value)} />
                <div className="text-danger">{errors.title}</div>
              </FormGroup>
            </Col>
          )}

          {(
            <Col md={4}>
              <FormGroup>
                <Label>Title(ar) *</Label>
                <Input placeholder="Enter title" value={title_ar} style={{ borderColor: "hsl(0, 0%, 80%)" }} onChange={(e) => setTitle_ar(e.target.value)} />
                <div className="text-danger">{errors.title_ar}</div>
              </FormGroup>
            </Col>
          )}

          <Col md={4}>
            <FormGroup>
              <Label>Sort Order *</Label>
              <Input type="number" placeholder="Enter sort order" value={sortOrder} style={{ borderColor: "hsl(0, 0%, 80%)" }} onChange={(e) => {
                if (e.target.value < 0) {
                  setSortOrder(0);
                } else {
                  setSortOrder(e.target.value)
                }
              }} />
              <div className="text-danger">{errors.sortOrder}</div>
            </FormGroup>
          </Col>

          {type === "Product_List" && (
            <>
              <Col md={6}>
                <FormGroup>
                  <Label>Collections *</Label>
                  <Select
                    options={collections.map((c) => ({
                      label: c.title,
                      value: c.id,
                    }))}
                    value={selectedCollections}
                    onChange={(vals) => setSelectedCollections(vals || [])}
                  />
                  <div className="text-danger">{errors.collections}</div>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Direction *</Label>
                  <Input type="select" value={selectedDirection} style={{ borderColor: "hsl(0, 0%, 80%)" }} onChange={(e) => setSelectedDirection(e.target.value)}>
                    <option value="">Select</option>
                    {DIRECTIONS.map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </Input>
                  <div className="text-danger">{errors.direction}</div>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Products *</Label>
                  <AsyncSelect
                    isMulti
                    loadOptions={debouncedLoadProducts}
                    value={selectedProducts}
                    onChange={(vals) => setSelectedProducts(vals || [])}
                  />
                  <div className="text-danger">{errors.products}</div>
                </FormGroup>
              </Col>
            </>
          )}

          {type == "Collection_List" && (
            <Col md={6}>
              <FormGroup>
                <Label>Collections *</Label>
                <Select
                  isMulti
                  options={collections.map((c) => ({
                    label: c.title,
                    value: c.id,
                  }))}
                  value={selectedCollections}
                  onChange={(vals) => setSelectedCollections(vals || [])}
                />
                <div className="text-danger">{errors.collections}</div>
              </FormGroup>
            </Col>
          )}

          {(type === "Banners" || type == "Product_List" || type == "Collection_List") && (
            <Col md={6}>
              <FormGroup>
                <Label>Visible items *</Label>
                <Input placeholder="Enter visible items count" type="number" value={visibleCount} style={{ borderColor: "hsl(0, 0%, 80%)" }} onChange={(e) => {
                  if (e.target.value < 0) {
                    setVisibleCount(0);
                  } else {
                    setVisibleCount(e.target.value)
                  }
                  }} />
                <div className="text-danger">{errors.visibleCount}</div>
              </FormGroup>
            </Col>
          )}
        </Row>

          {(type === "Sliders" || type === "Banners") &&
          sliders.map((slider, index) => {
            const sliderErrors = errors[`slider_${index}`] || {};
            return (
              <Card key={index} className="mb-3" style={{ border: "1px solid #1111" }}>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <Label>Banner Image *</Label>
                      <Input
                        type="file"
                        onChange={(e) =>
                          uploadImage(setUploadingIndex, handleSliderChange, e.target.files[0], index)
                        }
                      />
                      {slider.file && (
                        <img src={`https://bawaba-backend-h3n8.onrender.com${slider.file}`} width={100} className="mt-2" />
                      )}
                      <div className="text-danger">{sliderErrors.file}</div>
                    </Col>

                    <Col md={6}>
                      <Label>Link Type</Label>
                      <Input
                        type="select"
                        value={slider.linkType}
                        onChange={(e) =>
                          handleSliderChange(index, "linkType", e.target.value)
                        }
                      >
                        {LINK_TYPES.map((l) => (
                          <option key={l}>{l}</option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <Row className="mt-3">
                    {slider.linkType === "Product" && (
                      <>
                        <Label>Link</Label>
                        <Col md={6}>
                          <AsyncSelect
                            loadOptions={debouncedLoadProducts}
                            value={slider.products}
                            onChange={(val) =>
                              handleSliderChange(index, "products", val)
                            }
                          />
                          <div className="text-danger">{sliderErrors.products}</div>
                        </Col>
                      </>
                    )}

                    {slider.linkType === "Collection" && (
                      <>
                        <Label>Link</Label>
                        <Col md={6}>
                          <Select
                            options={collections.map((c) => ({
                              label: c.title,
                              value: String(c.id),
                            }))}
                            value={
                              slider.id
                                ? collections
                                  .filter((c) => String(c.id) === slider.id)
                                  .map((c) => ({ label: c.title, value: slider.id }))[0]
                                : null
                            }
                            onChange={(val) =>
                              handleSliderChange(index, "id", val.value)
                            }
                          />
                          <div className="text-danger">{sliderErrors.id}</div>
                        </Col>
                      </>
                    )}
                  </Row>

                  {sliders.length > 1 && (
                    <Button color="danger" size="sm" className="mt-3" onClick={() => removeSlider(index)}>
                      Remove
                    </Button>
                  )}
                </CardBody>
              </Card>
            );
          })}

        {(type === "Sliders" || type === "Banners") && (
          <Button style={{ float: "right" }} color="primary" onClick={addSlider}>
            + Add {type === "Banners" ? "Banner" : "Slider"}
          </Button>
        )}

        <div className="mt-4 d-flex gap-2">
          <button className="btn btn-primary" style={{ minWidth: "90px" }} type="submit">
            {isEditMode ? loading ? <ClipLoader color="white" size={17} /> : "Update" : loading ? <ClipLoader color="white" size={17} /> : "Save"}
          </button>

          {isEditMode && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={resetForm}
              style={{ padding: "8px", minWidth: "90px" }}
            >
              Cancel
            </button>
          )}
        </div>

      </Form>}

      <h4 className="mt-4 ms-3">Items</h4>

      <div className="">
        <div className="container-fluid">
          <TableContainer
            loading={loading}
            columns={columns}
            data={items || []}
            isGlobalFilter={true}
            isPagination={true}
            isDowload={true}
            SearchPlaceholder={t("Search")}
            pagination="pagination"
            docName="Roles"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        </div>
      </div>

    </div>
      {/* Confirmation Modal */ }
  <ConfirmationModal
    okText="Confirm"
    onCancel={() => setOpenModal(false)}
    onOk={handleDelete}
    isVisible={openModal}
    title="Delete Product"
    content="Are you sure you want to delete this item?"
  />
    </div >
  );
};

export default ItemList;
